import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { BENCHMARK_OPTIONS, DEFAULT_BENCHMARK_OPTION, DEFAULT_RESULTS_PER_PAGE, RESULTS_PER_PAGE_OPTIONS } from 'app/apps/discovery/app/constants';
import { DEFAULT_NUMBER_OF_RESULTS_REPORTS, NUMBER_OF_RESULTS_OPTIONS_REPORTS } from 'app/apps/reports/app/constants';
import { ALL_COMPARISONS, DEFAULT_COMPARE_TO, RESULTS_PER_PAGE_OPTIONS as STUDIO_RESULTS_PER_PAGE_OPTIONS, DEFAULT_RESULTS_PER_PAGE as STUDIO_DEFAULT_RESULTS_PER_PAGE } from 'app/apps/studio/app/constants';
import { QueryFilter } from 'app/hub-components/models/query-filter.model';
import { IFilterGroup, IQueryFilterWrapper } from '../../interfaces/query-filter.interface';
import { ColumnOrderEntry } from '../../interfaces/table-options.interface';
import { Query } from '../../models/analytics-api/query.model';
import { DateRangeValue } from '../../modules/date-range-picker/types';
import { serializeDateRangeValue } from '../../utils/date.util';
import { groupConnective } from '../../utils/filter.util';
import { QueryMigrationService } from '../migration/query-migration.service';
import { PublicLinkService } from '../public-link/public-link.service';
import { SessionService } from '../session/session.service';
import { TrackingService } from '../tracking/tracking.service';

const GLOBAL_FILTER_LEVEL = 'global';
const PAGE_FILTER_LEVEL = 'page-specific';

export interface OpenInReportsParams {
  query: Query;
  dateRange: DateRangeValue;
  globalFilters?: IFilterGroup;
  globalFiltersVersion?: number;
  excludeFalsy?: boolean;
  includeTotals?: boolean;
  columnOrder?: ColumnOrderEntry[];
}

export interface OpenInStudioParams {
  tables: {
    query: Query;
    excludeFalsy?: boolean;
    showColumnAllPagesTotals?: boolean;
    dateRange?: DateRangeValue;
    resultsPerPage?: number;
    benchmark?: string;
    benchmarkDateRange?: DateRangeValue;
    columnOrder?: ColumnOrderEntry[];
  }[];
  dateRange: DateRangeValue;
  globalFilters?: IFilterGroup;
  globalFiltersVersion?: number;
  columns?: 1 | 2;
}

export interface OpenInDiscoveryParams {
  query: Query;
  dateRange: DateRangeValue;
  excludeFalsy?: boolean;
  globalFilters?: IFilterGroup;
  resultsPerPage?: number;
  benchmark?: string;
  benchmarkDateRange?: DateRangeValue;
}

export type OpenSettled = (opened: boolean) => void;

@Injectable()
export class CrossAppNavigationService {
  private sessionService = inject(SessionService);
  private trackingService = inject(TrackingService);
  private queryMigrationService = inject(QueryMigrationService);
  private publicLinkService = inject(PublicLinkService);

  public openInReports(params: OpenInReportsParams): void {
    this.openInReportsOnceBuilt(of(params));
  }

  public openInReportsOnceBuilt(params$: Observable<OpenInReportsParams>, settled?: OpenSettled): void {
    this.trackingService.track('Open in Reports', { fromApp: this.sessionService.currentApp?.app });
    this.openInApp('reports', params$.pipe(map(params => this.reportData(params))), settled);
  }

  public openInStudio(params: OpenInStudioParams): void {
    if (!params.tables.length) {
      return;
    }
    this.openInStudioOnceBuilt(of(params));
  }

  public openInStudioOnceBuilt(params$: Observable<OpenInStudioParams>, settled?: OpenSettled): void {
    this.trackingService.track('Open in Studio', { fromApp: this.sessionService.currentApp?.app });
    this.openInApp('studio', params$.pipe(map(params => this.dashboardData(params))), settled);
  }

  public openInDiscovery({
    query,
    dateRange,
    excludeFalsy = false,
    globalFilters = [],
    resultsPerPage,
    benchmark,
    benchmarkDateRange,
  }: OpenInDiscoveryParams): void {
    const normalizedResultsPerPage = RESULTS_PER_PAGE_OPTIONS.find(o => o === resultsPerPage) ?? DEFAULT_RESULTS_PER_PAGE;
    const discoveryData = {
      name: 'New Discovery',
      query: this.discoveryQueryPayload(query, dateRange, globalFilters, benchmark, benchmarkDateRange),
      options: {
        dataOptions: { excludeFalsy },
        resultsPerPage: { value: normalizedResultsPerPage },
      },
    };

    this.trackingService.track('Open in Discovery', { fromApp: this.sessionService.currentApp?.app });
    this.openInApp('discovery', of(discoveryData));
  }

  private reportData({ query, dateRange, globalFilters = [], excludeFalsy = false, includeTotals = false, columnOrder }: OpenInReportsParams): object {
    const queryVersion = this.queryMigrationService.currentVersion;
    const numberOfResults = NUMBER_OF_RESULTS_OPTIONS_REPORTS.find(o => o === query.limit) ?? DEFAULT_NUMBER_OF_RESULTS_REPORTS;
    query.limit = numberOfResults;
    const datasetGroups = globalFilters.filter((gf: IQueryFilterWrapper) => gf.dataset === query.dataset && !!gf.expressions?.length);
    const dashboardGroups = datasetGroups
      .filter((gf: IQueryFilterWrapper) => gf.pageId === undefined)
      .map((gf: IQueryFilterWrapper) => ({ dataset: gf.dataset, ...this.serializedGroup(gf) }));
    const droppedLevels = datasetGroups.some((gf: IQueryFilterWrapper) => gf.pageId !== undefined) ? [PAGE_FILTER_LEVEL] : [];
    const reportData = {
      name: 'New Report',
      settings: {
        interval: serializeDateRangeValue(dateRange),
      },
      tables: [
        {
          name: 'Table 1',
          query: { ...this.unsignedQueryJson(query), version: queryVersion },
          options: {
            dataOptions: { excludeFalsy, includeTotals },
            dateOptions: { interval: null, tabSpecificDate: false },
            ...(columnOrder?.length ? { columnOrder } : {}),
          },
        },
      ],
      global_filters: {
        version: queryVersion,
        data: dashboardGroups,
      },
      ...(droppedLevels.length ? { droppedFiltersMessage: this.droppedFiltersMessage(droppedLevels, 'Reports') } : {}),
    };

    return reportData;
  }

  private dashboardData({
    tables,
    dateRange,
    globalFilters = [],
    globalFiltersVersion = 1,
    columns = 2,
  }: OpenInStudioParams): object {
    const canvasPreset = { value: 'large-laptop', width: 1440, height: 900 };
    const { width: canvasW, height: canvasH } = canvasPreset;
    const colCount = columns;
    const cellW = canvasW / colCount;
    const cellH = canvasH / colCount;
    const widgetsPerPage = colCount * colCount;

    const tableToWidget = (
      table: OpenInStudioParams['tables'][number],
      gridIndex: number,
    ) => {
      const { query, excludeFalsy = false, showColumnAllPagesTotals = false, dateRange: tableDateRange, resultsPerPage, benchmark, benchmarkDateRange, columnOrder } = table;
      const col = gridIndex % colCount;
      const row = Math.floor(gridIndex / colCount);
      const queryJson = this.queryJsonForStudioTableWidget(query);
      const compareTo = this.buildCompareTo(benchmark, benchmarkDateRange);
      const normalizedResultsPerPage = resultsPerPage !== undefined
        ? (STUDIO_RESULTS_PER_PAGE_OPTIONS.includes(resultsPerPage) ? resultsPerPage : STUDIO_DEFAULT_RESULTS_PER_PAGE)
        : undefined;
      return {
        type: 'table' as const,
        layout: {
          x: col * cellW,
          y: row * cellH,
          width: cellW,
          height: cellH,
        },
        inputs: queryJson,
        query: queryJson,
        options: {
          dataOptions: { excludeFalsy, showColumnAllPagesTotals },
          ...(tableDateRange ? { dateRange: { enabled: true, value: serializeDateRangeValue(tableDateRange) } } : {}),
          ...(normalizedResultsPerPage !== undefined ? { resultsPerPage: { value: normalizedResultsPerPage, name: normalizedResultsPerPage } } : {}),
          ...(compareTo ? { compareTo } : {}),
          ...(columnOrder?.length ? { columnOrder } : {}),
        },
      };
    };

    const pages = [];
    for (let offset = 0; offset < tables.length; offset += widgetsPerPage) {
      const pageTables = tables.slice(offset, offset + widgetsPerPage);
      const pageIndex = offset / widgetsPerPage;
      pages.push({
        id: pageIndex,
        name: `Page ${pageIndex + 1}`,
        widgets: pageTables.map((table, gridIndex) => tableToWidget(table, gridIndex)),
      });
    }

    const datasetSet = new Set(
      tables
        .map(t => t.query?.dataset)
        .filter((d): d is string => typeof d === 'string' && d.length > 0),
    );

    const globalFiltersData = globalFilters
      .filter((gf: IQueryFilterWrapper) => datasetSet.has(gf.dataset))
      .map((gf: IQueryFilterWrapper) => ({
        dataset: gf.dataset,
        connective: groupConnective(gf),
        expressions: gf.expressions.map((e: QueryFilter) => (e.asJson ? e.asJson() : e)),
      }));

    const dashboardData = {
      name: 'New Dashboard',
      settings: {
        backgroundColor: '#ffffff',
        size: {
          preset: {
            value: canvasPreset.value,
            width: canvasPreset.width,
            height: canvasPreset.height,
          },
          width: undefined,
          height: undefined,
        },
        gridSize: {
          value: 20,
        },
        interval: serializeDateRangeValue(dateRange),
      },
      pages,
      global_filters: {
        version: globalFiltersVersion,
        data: globalFiltersData,
      },
    };

    return dashboardData;
  }

  private openInApp(app: 'reports' | 'studio' | 'discovery', payload$: Observable<object>, settled?: OpenSettled): void {
    const baseUrl = this.sessionService.getAppBaseUrl(app);
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      settled?.(false);
      return;
    }

    payload$.pipe(
      switchMap(payload => this.publicLinkService.uuid().pipe(map(({ uuid }) => ({ payload, uuid })))),
      switchMap(({ payload, uuid }) => this.publicLinkService.create(payload, uuid, `/s/${uuid}`).pipe(map(() => uuid))),
    ).subscribe({
      next: uuid => {
        tab.location.href = `//${baseUrl}/new?stash=${uuid}`;
        settled?.(true);
      },
      error: () => {
        tab.close();
        settled?.(false);
      },
    });
  }

  private discoveryQueryPayload(
    query: Query,
    dateRange: DateRangeValue,
    globalFilters: IQueryFilterWrapper[] = [],
    benchmark?: string,
    benchmarkDateRange?: DateRangeValue,
  ) {
    const jsonQuery = this.unsignedQueryJson(query);
    const { order, filters, metric_filters: metricFilters, dimension_properties: dimensionProperties, ...rest } = jsonQuery;
    const advFilters = filters?.expressions ?? [];
    const datasetGroups = (globalFilters ?? []).filter(gf => gf.dataset === query.dataset && !!gf.expressions?.length);
    const droppedLevels = [
      ...(datasetGroups.some(gf => gf.pageId === undefined) ? [GLOBAL_FILTER_LEVEL] : []),
      ...(datasetGroups.some(gf => gf.pageId !== undefined) ? [PAGE_FILTER_LEVEL] : []),
    ];
    return {
      ...rest,
      ...(dimensionProperties ? { dimensionProperties } : {}),
      order: this.orderForDiscoveryLink(order),
      ...(advFilters.length ? { advFilters } : {}),
      ...(droppedLevels.length ? { droppedFiltersMessage: this.droppedFiltersMessage(droppedLevels, 'Discovery') } : {}),
      ...(filters?.connective ? { advFiltersConnective: filters.connective } : {}),
      ...(metricFilters?.expressions?.length ? { metricFilters: metricFilters.expressions } : {}),
      ...(metricFilters?.connective ? { metricFiltersConnective: metricFilters.connective } : {}),
      dates: serializeDateRangeValue(dateRange),
      drilldowns: {},
      ...(BENCHMARK_OPTIONS.some(o => o.value === benchmark && o.value !== DEFAULT_BENCHMARK_OPTION.value) ? { benchmark } : {}),
      ...(benchmarkDateRange ? { benchmarkDateRange: serializeDateRangeValue(benchmarkDateRange) } : {}),
    };
  }

  private droppedFiltersMessage(levels: string[], app: string): string {
    const names = levels.join(' and ');
    const source = this.sessionService.currentApp?.app === 'studio' ? 'dashboard' : 'report';
    return `${names.charAt(0).toUpperCase()}${names.slice(1)} filtering is not supported in ${app}. ` +
      `Please add the ${names} filters from the ${source} using the regular filtering modal in the query builder.`;
  }

  private serializedGroup(group: IQueryFilterWrapper) {
    return {
      connective: groupConnective(group),
      expressions: group.expressions.map((e: QueryFilter) => (e.asJson ? e.asJson() : e)),
    };
  }

  private orderForDiscoveryLink(order: string | string[] | undefined): string {
    if (typeof order === 'string') {
      return order;
    }
    if (Array.isArray(order)) {
      const first = order.find((o): o is string => typeof o === 'string' && o.length > 0);
      return first ?? '';
    }
    return '';
  }

  private buildCompareTo(benchmark?: string, benchmarkDateRange?: DateRangeValue) {
    const match = ALL_COMPARISONS.find(o => o.value === benchmark && o.value !== DEFAULT_COMPARE_TO.value);
    if (!match) {
      return null;
    }
    return {
      value: match.value,
      name: match.name,
      ...(benchmarkDateRange ? { dateRange: serializeDateRangeValue(benchmarkDateRange) } : {}),
    };
  }

  private queryJsonForStudioTableWidget(query: Query) {
    const json = this.unsignedQueryJson(query);
    if (json.filters?.expressions?.length > 0) {
      return { ...json, version: Math.max(json.version ?? 0, 1) };
    }
    return json;
  }

  private unsignedQueryJson(query: Query): Record<string, any> {
    const json: Record<string, any> = { ...query.asJson() };
    delete json.selection;
    delete json.metric_selection;
    return json;
  }
}
